import { useCallback, useEffect, useRef, useState } from 'react';
import { attendanceLocationSchema, isWithinPublicAttendanceRadius, PUBLIC_ATTENDANCE_RADIUS_METERS } from 'shared-models/browser';
import type { AttendanceActionType, AttendanceLocation, PublicAttendanceLocation, PublicAttendanceActionView, PublicAttendanceEmployee, PublicAttendancePrinter, PublicAttendanceSlipRequest } from 'shared-models/browser';
import { attendancePublicService as api } from '../../services/attendance/attendance-public.service';
import { captureReceiptSnapshot } from '../PosShellPage/components/receipt/shared/receipt.utils';
import { usePublicAttendanceLocation } from './usePublicAttendanceLocation';

function radiusError(location: PublicAttendanceLocation): string {
  return location.accuracy > PUBLIC_ATTENDANCE_RADIUS_METERS
    ? `התקבל מיקום משוער בלבד (דיוק של כ-${Math.ceil(location.accuracy)} מטר). לא ניתן לאמת טווח של ${PUBLIC_ATTENDANCE_RADIUS_METERS} מטר. נסו ממכשיר בעל מיקום מדויק יותר`
    : 'הכניסה למשמרת אפשרית רק בטווח 500 מטר מהמיקום שהוגדר. יש לוודא שמיקום מדויק פעיל';
}

export function usePublicAttendanceController(tenantId: string, key: string) {
  const locationState = usePublicAttendanceLocation();
  const [locationCenter, setLocationCenter] = useState<AttendanceLocation | null | undefined>(undefined);
  const [employees, setEmployees] = useState<PublicAttendanceEmployee[]>([]);
  const [nextCursor, setNextCursor] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [retry, setRetry] = useState(0);
  const [slip, setSlip] = useState<PublicAttendanceActionView | null>(null);
  const [printers, setPrinters] = useState<PublicAttendancePrinter[] | null>(null);
  const [printing, setPrinting] = useState(false);
  const slipRef = useRef<HTMLDivElement>(null);
  const slipCredentials = useRef<PublicAttendanceSlipRequest | null>(null);
  const printLock = useRef(false);
  const pageLock = useRef(false);
  const clockLock = useRef(false);
  const revision = useRef(0);
  const load = useCallback(async (cursor: number, replace: boolean, signal?: AbortSignal) => {
    if (pageLock.current) return;
    pageLock.current = true; setLoading(true); setError('');
    const started = revision.current;
    try {
      if (!key) throw new Error('קישור הנוכחות אינו תקין. פנו למנהל.');
      const page = await api.employees(tenantId, key, cursor, signal);
      if (started !== revision.current || signal?.aborted) return;
      const center = attendanceLocationSchema.safeParse(page.publicAttendanceLocation);
      if (page.publicAttendanceLocation !== null && !center.success) throw new Error('לא ניתן לאמת את מיקום העסק. יש לרענן ולנסות שוב');
      setLocationCenter(center.success ? center.data : null);
      setEmployees((previous) => replace ? page.employees : [...previous, ...page.employees]);
      setNextCursor(page.nextCursor); setAvailable(true);
    } catch (failure) {
      if (started === revision.current && !signal?.aborted) {
        setLocationCenter(undefined);
        setError(failure instanceof Error ? failure.message : 'טעינת העובדים נכשלה');
      }
    } finally { if (started === revision.current) { pageLock.current = false; setLoading(false); } }
  }, [tenantId, key]);

  useEffect(() => {
    const abort = new AbortController();
    revision.current++; pageLock.current = false; setAvailable(false); setEmployees([]); setNextCursor(null); setNotice('');
    setLocationCenter(undefined);
    slipCredentials.current = null; setSlip(null); setPrinters(null); setPrinting(false);
    void load(0, true, abort.signal);
    return () => { abort.abort(); revision.current++; slipCredentials.current = null; };
  }, [load, retry]);

  const reportRequired = () => setError('יש להפיק דוח עובד בקופה לפני היציאה ממשמרת');
  const clock = async (employeeId: number, pin: string, actionType: AttendanceActionType, departmentName?: string) => {
    if (clockLock.current) return false;
    clockLock.current = true; setBusy(true); setError(''); setNotice('');
    const started = revision.current;
    try {
      const location = actionType === 'clock_in' ? await locationState.currentLocation() : undefined;
      if (started !== revision.current) return false;
      if (actionType === 'clock_in') {
        if (locationCenter === undefined || !location) throw new Error('לא ניתן לאמת את מיקום העסק. יש לרענן ולנסות שוב');
        if (locationCenter && !isWithinPublicAttendanceRadius(locationCenter, location)) throw new Error(radiusError(location));
      }
      const action = await api.clock(tenantId, key, { employeeId, pin, actionType, ...(departmentName ? { departmentName } : {}), ...(location ? { location } : {}) });
      if (started !== revision.current) return false;
      slipCredentials.current = { employeeId, pin, actionId: action.id };
      setSlip(action);
      setNotice(action.actionType === 'clock_in' ? 'הכניסה למשמרת נרשמה' : 'היציאה ממשמרת נרשמה');
      return true;
    } catch (failure) {
      if (started === revision.current) setError(failure instanceof Error ? failure.message : 'רישום הנוכחות נכשל');
      return false;
    } finally { clockLock.current = false; if (started === revision.current) setBusy(false); }
  };

  const closeSlip = () => {
    if (printLock.current) return;
    slipCredentials.current = null; setSlip(null); setPrinters(null); setNotice(''); setError('');
  };
  const printSlip = async (selectedPrinterId?: number) => {
    if (printLock.current || !slipCredentials.current || !slipRef.current) return;
    printLock.current = true; setPrinting(true); setError('');
    const started = revision.current;
    const credentials = slipCredentials.current;
    try {
      let printerId = selectedPrinterId;
      if (printerId === undefined) {
        const availablePrinters = await api.slipPrinters(tenantId, key, credentials);
        if (started !== revision.current) return;
        if (!availablePrinters.length) throw new Error('אין מדפסות חשבונות פעילות. אישור הנוכחות נשמר במסך.');
        if (availablePrinters.length > 1) { setPrinters(availablePrinters); return; }
        printerId = availablePrinters[0].id;
      }
      const image = await captureReceiptSnapshot(slipRef.current);
      if (started !== revision.current) return;
      if (!image) throw new Error('לא ניתן ליצור תמונה להדפסה. נסו שוב.');
      await api.printSlip(tenantId, key, { ...credentials, printerId, printableImageDataUrl: image });
      if (started !== revision.current) return;
      slipCredentials.current = null; setPrinters(null); setSlip(null);
      setNotice('הנוכחות נרשמה ואישור הנוכחות נשלח להדפסה');
    } catch (failure) {
      if (started === revision.current) {
        setPrinters(null);
        setError(failure instanceof Error ? failure.message : 'שליחת אישור הנוכחות להדפסה נכשלה');
      }
    } finally { printLock.current = false; if (started === revision.current) setPrinting(false); }
  };
  return { employees, loading, available, busy, error, notice, clock, reportRequired, slip, slipRef, printers, printing, printSlip, closeSlip,
    locationReady: locationCenter !== undefined && locationState.location !== null
      && (locationCenter === null || isWithinPublicAttendanceRadius(locationCenter, locationState.location)),
    locationError: locationState.locationError || (locationCenter && locationState.location
      && !isWithinPublicAttendanceRadius(locationCenter, locationState.location) ? radiusError(locationState.location) : ''),
    locationInstructions: locationState.locationInstructions,
    retryLocation: locationState.retryLocation,
    closePrinterPicker: () => { if (!printLock.current) setPrinters(null); },
    status: (employeeId: number) => api.status(tenantId, key, employeeId),
    clearError: () => { setError(''); setNotice(''); },
    refresh: () => setRetry((previous) => previous + 1),
    hasMore: nextCursor !== null,
    loadMore: () => { if (nextCursor !== null) void load(nextCursor, false); },
  };
}